import * as React from 'react';
import { memo, useCallback, ChangeEvent } from 'react';
import { LucideIcon } from 'lucide-react';

import styled, { css } from 'styled-components';

import { mq } from 'components/constants/breakpoints';

import { Button, Icon, IconsNames, Input } from 'atoms';

export interface ISearch {
  value: string;
  onChange: (value: string) => void;
  onSearch?: () => void;
  onFocus?: () => void;
  clear?: () => void;
  placeholder?: string;
  buttonProps?: {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    /** Optional leading lucide icon rendered before the button label. */
    icon?: LucideIcon;
  };
}

export const Search = memo(
  ({
    value,
    onChange,
    onSearch,
    onFocus,
    clear,
    buttonProps,
    placeholder,
  }: ISearch) => {
    const onChangeHandler = useCallback(
      ({ target: { value: targetValue } }: ChangeEvent<HTMLInputElement>) => {
        onChange(targetValue);
      },
      [onChange],
    );

    return (
      <SearchStyled>
        {value ? (
          <CloseIconWrapper onClick={clear}>
            <Icon name={IconsNames.CloseIcon} />
          </CloseIconWrapper>
        ) : (
          <SearchIconWrapper onClick={onSearch}>
            <Icon name={IconsNames.SearchIcon} />
          </SearchIconWrapper>
        )}
        <InputWrapper>
          <InputRestyled
            type="text"
            placeholder={placeholder || 'Enter any text...'}
            value={value}
            onChange={onChangeHandler}
            onEnter={onSearch}
            onFocus={onFocus}
          />
        </InputWrapper>
        {buttonProps && (
          <ButtonWrapper>
            <ButtonRestyled
              colors={{
                color: 'var(--k-fg-ffffff)',
                backgroundColor: 'var(--k-bg-0d53fc)',
              }}
              onClick={buttonProps.onClick}
              disabled={buttonProps.disabled}
            >
              {buttonProps.icon ? (
                <ButtonInner>
                  {React.createElement(buttonProps.icon, {
                    size: 16,
                    strokeWidth: 2,
                  })}
                  <span>{buttonProps.label}</span>
                </ButtonInner>
              ) : (
                buttonProps.label
              )}
            </ButtonRestyled>
          </ButtonWrapper>
        )}
      </SearchStyled>
    );
  },
);

const SearchStyled = styled.div`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  padding: 8px 8px 8px 24px;
  border-radius: 8px;
  background: var(--k-bg-ffffff);
  border: 1px solid var(--k-border-ebeff2);

  /* ТЗ 0121: на телефоне кнопка («Добавить студента») уходит под поле на
     всю ширину — иначе поле сжималось до «Поиск студ». */
  ${mq.mobile} {
    flex-wrap: wrap;
    padding: 6px 6px 6px 14px;
    row-gap: 6px;
  }
`;

const InputRestyled = styled(Input)`
  border: none;
  line-height: 17px;
  padding: 11px 16px 11px 12px;
  :focus {
    outline: none;
  }
`;

const ButtonRestyled = styled(Button)`
  white-space: nowrap;
  min-height: 40px;
  padding: 0 16px 0 16px;
  font-size: 13px;
  line-height: 16px;
`;

const baseWrapperStyles = css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const InputWrapper = styled.div`
  ${baseWrapperStyles}
  width: 100%;

  ${mq.mobile} {
    flex: 1 1 0;
    min-width: 0;
  }
`;

const ButtonWrapper = styled.div`
  ${baseWrapperStyles}

  ${mq.mobile} {
    flex: 1 1 100%;

    & > button {
      width: 100%;
      min-height: 44px;
    }
  }
`;

const ButtonInner = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const CloseIconWrapper = styled.div`
  ${baseWrapperStyles}
`;

const SearchIconWrapper = styled.div`
  ${baseWrapperStyles}
`;
